import { Transition } from '@headlessui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import GraphicControlForm from 'src/components/GraphicControlForm';
import GraphicMediaLibrary from 'src/components/GraphicMediaLibrary';
import GraphicPropertiesForm from 'src/components/GraphicPropertiesForm';
import SportData, { getPropertyOptionsSportData } from 'src/components/SportData';
import { DataConnectorPanel } from 'src/features/dataConnector';
import { useAutoUpdateSnapshot } from 'src/features/dataConnector/application/autoUpdateManager';
import useKeypress from 'src/hooks/useKeypress';
import { StateType } from 'src/redux/reducers';
import { REQUEST_UPDATE_PLAYLIST_GRAPHIC } from 'src/redux/reducers/playlistGraphic';

import styles from './GraphicDetails.module.scss';
import { getMappedProperties, getPropertiesValues, LocalProperties } from './utils';

export default function GraphicDetails() {
  const dispatch = useDispatch();
  const [mediaLibraryState, setMediaLibraryState] = useState<{
    open: boolean;
    onClose?: (url: string) => void;
  }>({ open: false, onClose: () => {} });
  const [showSportData, setShowSportData] = useState<boolean>(false);
  const [showDataConnector, setShowDataConnector] = useState<boolean>(false);
  const [canHideLibrary, setCanHideLibrary] = useState<boolean>(true);

  const { selectedPlaylistGraphicId, playlistGraphics, selectedPlaylistGraphic } = useSelector(
    (state: StateType) => state.playlistGraphic
  );

  const selectedGraphic = useMemo(
    () =>
      playlistGraphics?.find((graphic) => graphic.id === selectedPlaylistGraphicId) ||
      selectedPlaylistGraphic,
    [playlistGraphics, selectedPlaylistGraphic, selectedPlaylistGraphicId]
  );

  const autoUpdateSnapshot = useAutoUpdateSnapshot(selectedGraphic?.id);
  const [localProperties, setLocalProperties] = useState<LocalProperties | null>(null);
  const selectedGraphicRef = useRef<typeof selectedGraphic>(selectedGraphic);
  const [pendingPropertyUpdates, setPendingPropertyUpdates] = useState<{
    preview: boolean;
    live: boolean;
  }>({ preview: false, live: false });

  const handleGraphicUpdated = (target: 'preview' | 'live') => {
    setPendingPropertyUpdates((current) => ({ ...current, [target]: false }));
  };

  const handleClickOutsideDrawer = () => {
    setMediaLibraryState({ open: false, onClose: () => {} });
    setShowSportData(false);
    setShowDataConnector(false);
  };

  useEffect(() => {
    selectedGraphicRef.current = selectedGraphic;
  }, [selectedGraphic]);

  useEffect(() => {
    handleClickOutsideDrawer();
    setLocalProperties(getMappedProperties(selectedGraphic));
    setPendingPropertyUpdates({ preview: false, live: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGraphic?.id]);

  useEffect(() => {
    if (!selectedGraphic || !autoUpdateSnapshot.isEnabled) {
      return;
    }

    setLocalProperties(getMappedProperties(selectedGraphic));
  }, [autoUpdateSnapshot.isEnabled, selectedGraphic]);

  const dispatchPropertyValuesUpdate = (
    propertiesValues: PropertyValue,
    options?: { requestDelay?: number; updateLive?: boolean }
  ) => {
    const currentGraphic = selectedGraphicRef.current;
    if (!currentGraphic) return;

    dispatch({
      type: REQUEST_UPDATE_PLAYLIST_GRAPHIC,
      payload: {
        graphic: {
          ...currentGraphic,
          propertiesValues,
        },
        ...options,
      },
    });
  };

  const handleUpdateProperties = (properties: LocalProperties) => {
    setLocalProperties(properties);
    setPendingPropertyUpdates({ preview: true, live: true });
    dispatchPropertyValuesUpdate(getPropertiesValues(properties), { requestDelay: 250 });
  };

  const handleRequestSportData = () => {
    setShowSportData(true);
  };

  const handleRequestDataConnector = () => {
    setShowDataConnector(true);
  };

  const handleImportSportData = (propertyId: string, value: string | PropertyValue[]) => {
    if (!propertyId || !localProperties) {
      setShowSportData(false);
      return;
    }

    const property = localProperties[propertyId];
    if (!property) {
      setShowSportData(false);
      return;
    }

    const updatedLocalProperties: LocalProperties = {
      ...localProperties,
      [propertyId]: { ...property, value },
    };

    handleUpdateProperties(updatedLocalProperties);
    setShowSportData(false);
  };

  const handleImportDataConnector = (importedValues: PropertyValue) => {
    if (!localProperties) {
      setShowDataConnector(false);
      return;
    }

    const updatedLocalProperties: LocalProperties = {
      ...localProperties,
      ...Object.entries(importedValues).reduce<LocalProperties>((acc, [propertyId, value]) => {
        const property = localProperties[propertyId];
        if (property) {
          acc[propertyId] = { ...property, value };
        }
        return acc;
      }, {}),
    };

    setLocalProperties(updatedLocalProperties);
    setPendingPropertyUpdates({ preview: true, live: true });
    dispatchPropertyValuesUpdate(getPropertiesValues(updatedLocalProperties), {
      requestDelay: 250,
    });
    setShowDataConnector(false);
  };

  const handleSelectMedia = (
    properties: Properties,
    handleSelectValue?: (value: string) => void
  ) => {
    const onClose = (url: string) => {
      if (handleSelectValue) {
        handleSelectValue(url);
      } else {
        handleUpdateProperties({
          ...localProperties,
          [properties.id]: { ...properties, value: url },
        });
      }

      if (selectedGraphic && !selectedGraphic.graphic.config.isSwappable) {
        setMediaLibraryState({ open: false, onClose: () => {} });
      }
    };

    setMediaLibraryState({ open: true, onClose });
  };

  const handleMediaSelected = (url: string) => {
    mediaLibraryState.onClose?.(url);
  };

  useKeypress('Escape', {}, () => canHideLibrary && handleClickOutsideDrawer());

  if (!selectedGraphic) return null;

  const slideOutOpen = mediaLibraryState.open || showSportData || showDataConnector;

  return (
    <div className={styles.Container}>
      <GraphicControlForm
        selectedGraphic={selectedGraphic}
        showStatsPerform={
          getPropertyOptionsSportData(
            selectedGraphic.graphic.ref,
            selectedGraphic.graphic.properties
          ).length > 0
            ? handleRequestSportData
            : undefined
        }
        showDataConnector={
          selectedGraphic.graphic.config.dataConnector?.providerId !== undefined
            ? handleRequestDataConnector
            : undefined
        }
        isAutoUpdateEnabled={autoUpdateSnapshot.isEnabled}
      />
      <div className={styles.Content}>
        {!autoUpdateSnapshot.isEnabled && (
          <GraphicPropertiesForm
            selectedGraphic={selectedGraphic}
            pendingPropertyUpdates={pendingPropertyUpdates}
            onGraphicUpdated={handleGraphicUpdated}
            properties={localProperties}
            handleChangeGraphicProperties={handleUpdateProperties}
            handleSelectMedia={handleSelectMedia}
          />
        )}
        <Transition.Root show={slideOutOpen} className={styles.SlideOutContainer}>
          <Transition.Child
            className={styles.Backdrop}
            onClick={handleClickOutsideDrawer}
            as="div"
            leave="transition-opacity"
            leaveFrom="opacity-25"
            leaveTo="opacity-0"
          />

          <Transition.Child
            className={styles.SlideOutContent}
            enter="transition-all duration-100"
            enterFrom="transform translate-x-[75%]"
            enterTo="transform translate-x-0"
            leave="transition-all duration-100"
            leaveFrom="transform translate-x-0"
            leaveTo="transform translate-x-[75%]"
          >
            {mediaLibraryState.open && (
              <GraphicMediaLibrary
                setCanHideLibrary={setCanHideLibrary}
                onFileSelect={handleMediaSelected}
              />
            )}

            {showSportData && (
              <SportData selectedGraphic={selectedGraphic} onImport={handleImportSportData} />
            )}

            {showDataConnector && (
              <DataConnectorPanel
                selectedGraphic={selectedGraphic}
                onImport={handleImportDataConnector}
              />
            )}
          </Transition.Child>
        </Transition.Root>
      </div>
    </div>
  );
}
